import { FC, useContext, useEffect, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useFetchServerPaginatedBulkSelectionIds,
  useLiveMapAutofitBulkGate,
  usePageContext,
} from "@ryde/redux";
import {
  addIdsToTableSelection,
  BulkGridSelectionModeEnum,
  isEntityServerSidePaginated,
  isEntityWithGridMapBulkAutofit,
  isTableGroupIdsSelectedSF,
  removeIdsFromTableSelection,
  resolvePassengerBulkSelectionGroupFilter,
  selectedIdsSF,
  selectedTableRowsIdsSetSF,
  starredTableRowsIdsAtomFamily,
  tableGroupIdsSF,
  tableGroupingFieldResolvedSF,
  warnTableSelectionLimitReached,
  wouldExceedTableSelectionLimit,
} from "@ryde/shared";

import { TableItemContext, TableWrapperContext } from "../table";
import { MultiIconHeaderContent } from "./head-cell.generic.multi-icon.content";

interface IMultiIconHeader {
  dataIds?: number[];
}

export const MultiIconHeader: FC<IMultiIconHeader> = ({ dataIds }) => {
  const { entity } = usePageContext();
  const { groupingField } = useContext(TableWrapperContext);
  const { groupingValue } = useContext(TableItemContext);
  const isGridMapBulkAutofitEntity = isEntityWithGridMapBulkAutofit(entity);
  const isServerPaginated = isEntityServerSidePaginated(entity);
  const { fetchBulkSelectionIds } = useFetchServerPaginatedBulkSelectionIds();
  const groupingFieldResolved = useRecoilValue(
    tableGroupingFieldResolvedSF(entity),
  );
  const { runBulkSelectionDeferringMapRender } = useLiveMapAutofitBulkGate();
  const [optimisticChecked, setOptimisticChecked] = useState<boolean | null>(
    null,
  );

  const [starredIds, setStarredIds] = useRecoilState(
    starredTableRowsIdsAtomFamily(entity),
  );

  const isCheckboxChecked = useRecoilValue(
    isTableGroupIdsSelectedSF([entity, groupingField, groupingValue]),
  );

  const setSelectedIds = useSetRecoilState(selectedIdsSF(entity));
  const selectedIdsSet = useRecoilValue(selectedTableRowsIdsSetSF(entity));

  const groupedIds = useRecoilValue(
    tableGroupIdsSF([entity, groupingField, groupingValue]),
  );

  const isCheckboxDisplayedChecked = optimisticChecked ?? isCheckboxChecked;

  useEffect(() => {
    setOptimisticChecked(null);
  }, [isCheckboxChecked]);

  const applySelectedIdsUpdate = useEvent((scopeIds: number[]): void => {
    setSelectedIds(ids => {
      if (!isCheckboxChecked) {
        return addIdsToTableSelection(ids, scopeIds, entity);
      }

      return removeIdsFromTableSelection(ids, scopeIds);
    });
  });

  const resolveHeaderScopeIds = useEvent(async (): Promise<number[]> => {
    if (!isServerPaginated) {
      return groupedIds;
    }

    const groupFilter = resolvePassengerBulkSelectionGroupFilter({
      groupingField: groupingFieldResolved ?? groupingField,
      groupingValue,
    });
    const fetched = await fetchBulkSelectionIds({
      mode: BulkGridSelectionModeEnum.All,
      ...groupFilter,
    });

    return fetched ?? groupedIds;
  });

  const onCheckboxClick = useEvent((): void => {
    void (async () => {
      setOptimisticChecked(!isCheckboxDisplayedChecked);

      const scopeIds = await resolveHeaderScopeIds();

      if (
        !isCheckboxChecked &&
        wouldExceedTableSelectionLimit(
          Array.from(selectedIdsSet),
          scopeIds,
          entity,
        )
      ) {
        warnTableSelectionLimitReached(entity);
        setOptimisticChecked(null);

        return;
      }

      const applySelection = (): void => {
        applySelectedIdsUpdate(scopeIds);
      };

      if (isGridMapBulkAutofitEntity) {
        runBulkSelectionDeferringMapRender(applySelection);

        return;
      }

      applySelection();
    })();
  });

  const dataIdSet = useMemo(() => new Set(dataIds), [dataIds]);

  const handleStarredClick = useEvent(() => {
    const hasAlreadyStarred = starredIds.some(id => dataIdSet.has(id));

    if (hasAlreadyStarred) {
      setStarredIds(ids => ids.filter(id => !dataIdSet.has(id)));
    } else {
      setStarredIds(ids => Array.from(new Set([...ids, ...(dataIds || [])])));
    }
  });

  const starredChecked = useMemo(() => {
    return starredIds.some(id => dataIdSet.has(id));
  }, [starredIds, dataIdSet]);

  return (
    <MultiIconHeaderContent
      onCheckboxClick={onCheckboxClick}
      onStarClick={handleStarredClick}
      isStarredChecked={starredChecked}
      isCheckboxChecked={isCheckboxDisplayedChecked}
    />
  );
};
